import {
  BarChart3, BookOpen, Calendar, CalendarDays, CalendarRange, ClipboardList, DollarSign, Download,
  FolderTree, LayoutGrid, ListChecks, Megaphone, MessageSquare, Network, PackageSearch, Palette, Plug, Scale, ShieldCheck, ShoppingCart, Target, Truck, Users, Warehouse,
  type LucideIcon,
} from "lucide-react";
import {
  dashboard_path,
  admin_design_system_path,
  admin_glossary_path,
  admin_claude_questions_path,
  admin_imports_path,
  admin_integrations_path,
  org_revenue_path,
  inventory_overview_path,
  purchase_products_path,
  purchase_incoming_path,
  market_path,
  revenue_path,
  org_nodes_path,
  product_nodes_path,
  team_members_path,
  team_roles_path,
  tasks_path,
  pick_market_path,
} from "@/lib/routes";
import type { Market } from "@/lib/useMarket";

export type NavLink = {
  label: string;
  href: string;
  icon?: LucideIcon;
  // One line under the label, in the Menu panel and on the Admin page.
  description?: string;
  // Lit on URLs under this prefix instead of under href.
  activePrefix?: string;
  // Lit on this exact URL only: the market overview (its href starts every
  // market URL) and picker links (they differ only in their query).
  exactOnly?: boolean;
  // More URL prefixes that light this link: Home, Revenue and Inventory Overview
  // point at the remembered market but stay lit on their all-markets pages.
  alsoActiveOn?: string[];
};

// A section with an icon is one of the product's three parts (Planning,
// Purchase, Push): its own column in the Menu panel, with a large heading and
// a one-line `description`. Without one, a small grey heading in the panel's
// first column; an empty heading shows none. `pageTabs` puts the section's
// links as tabs in the page title row of each of its pages (Planning has its
// own tabs, PlanTabs).
export type NavSection = { heading: string; icon?: LucideIcon; description?: string; pageTabs?: boolean; links: NavLink[] };

export type NavOptions = {
  // The remembered market (shared prop remembered_market).
  market: Market | null;
  // The last switch was to All markets (shared prop all_markets_picked).
  allMarketsPicked: boolean;
  wholesaleMarketIds: string[];
  canManageOrgNodes: boolean;
  canManageProductGroups: boolean;
  canManageWarehouses: boolean;
  canManageRoles: boolean;
};

// A page inside a market: the remembered market's, or the market picker,
// which opens the same section once a market is picked. `section` must be on
// MarketPickerController::SECTIONS.
function marketLink(market: Market | null, label: string, icon: LucideIcon, section: string, description?: string): NavLink {
  if (!market) return { label, icon, description, href: pick_market_path({ to: section }), exactOnly: true };
  return { label, icon, description, href: `${market_path(market.slug)}${section}` };
}

// The Menu panel's sections, the same on every page. Market pages follow the remembered market, or go to
// the picker when there is none. Home, Revenue and Inventory Overview, which also
// have an all-markets page, follow the last switch: All markets, or the
// remembered market; with neither they open their all-markets pages.
export function buildNavLinks(o: NavOptions): NavSection[] {
  const m = o.market;
  const either = o.allMarketsPicked ? null : m;
  // A remembered market without a wholesale channel hides the Wholesale
  // links; with none remembered they show and lead to the picker.
  const showWholesale = !m || o.wholesaleMarketIds.includes(m.id);
  const home = { label: "Home", icon: LayoutGrid, description: "How the business is doing." };
  const revenue = { label: "Revenue", icon: BarChart3, description: "What actually sold." };
  const inventory = { label: "Inventory Overview", icon: Warehouse, description: "Stock per warehouse and when it runs out." };

  return [
    {
      heading: "",
      links: [
        either
          ? { ...home, href: market_path(either.slug), exactOnly: true, alsoActiveOn: [dashboard_path()] }
          : { ...home, href: dashboard_path() },
        { label: "My Work", icon: ListChecks, description: "Your tasks.", href: tasks_path() },
      ],
    },
    {
      heading: "Planning",
      icon: Target,
      description: "Sales targets and forecasts for each market.",
      links: [
        marketLink(m, "Top Down", Target, "/plan/top-down", "Revenue targets split across channels and months."),
        marketLink(m, "Products & Pricings", DollarSign, "/plan/product-pricings", "Planned prices per product and channel."),
        marketLink(m, "By Month", CalendarDays, "/plan/bottom-up/by-month", "Units per product and month."),
        marketLink(m, "By Day", Calendar, "/plan/bottom-up/by-day", "Units per product and day."),
        // Wholesale Customers opens from a button on the Wholesale Forecast page.
        ...(showWholesale
          ? [marketLink(m, "Wholesale Forecast", CalendarRange, "/wholesale/forecast-by-account", "Wholesale units per account and month.")]
          : []),
      ],
    },
    {
      heading: "Purchase",
      icon: ShoppingCart,
      description: "What to buy, and what is on its way.",
      pageTabs: true,
      links: [
        { label: "Purchase Plan", icon: ClipboardList, description: "The products being bought, all markets.", href: purchase_products_path() },
        { label: "Incoming / PO", icon: Truck, description: "Purchase orders and stock on its way.", href: purchase_incoming_path() },
      ],
    },
    {
      heading: "Push",
      icon: Megaphone,
      description: "Products with too much stock.",
      links: [
        marketLink(m, "Review", PackageSearch, "/push/review", "Overstocked products and what to do about them."),
      ],
    },
    {
      heading: "Inventory",
      links: [
        either
          ? { ...inventory, href: inventory_overview_path({ market_slug: either.slug }), alsoActiveOn: [inventory_overview_path()] }
          : { ...inventory, href: inventory_overview_path() },
      ],
    },
    {
      heading: "Performance",
      links: [
        either
          ? { ...revenue, href: revenue_path(either.slug), alsoActiveOn: [org_revenue_path()] }
          : { ...revenue, href: org_revenue_path() },
        marketLink(m, "Plan vs Actual", Scale, "/plan/actuals", "What sold against what was planned."),
      ],
    },
  ];
}

// The avatar menu's Settings group.
export function buildSettingsLinks(o: NavOptions): NavLink[] {
  return [
    ...(o.canManageOrgNodes ? [{ label: "Org Structure", icon: Network, href: org_nodes_path() }] : []),
    ...(o.canManageProductGroups ? [{ label: "Product groups", icon: FolderTree, href: product_nodes_path() }] : []),
    { label: "Team", icon: Users, href: team_members_path() },
    ...(o.canManageRoles ? [{ label: "Roles", icon: ShieldCheck, href: team_roles_path() }] : []),
    ...(o.canManageWarehouses ? [marketLink(o.market, "Warehouses", Warehouse, "/warehouses")] : []),
  ];
}

// Internal pages listed on the Admin page (Super Admins only).
export function buildAdminLinks(): NavLink[] {
  return [
    { label: "Import", icon: Download, href: admin_imports_path(), description: "Data sources and their syncs" },
    { label: "Integrations", icon: Plug, href: admin_integrations_path(), description: "Slack and Google connections" },
    { label: "Design System", icon: Palette, href: admin_design_system_path(), description: "Colors, text styles and components used across Atlas" },
    { label: "Glossary", icon: BookOpen, href: admin_glossary_path(), description: "What each stock and sales number means, and how it is counted" },
    { label: "Claude questions", icon: MessageSquare, href: admin_claude_questions_path(), description: "What people asked Claude about Atlas, and the tools it called" },
  ];
}

// The href of the link to light for `url`. An exact match wins; otherwise the
// longest matching prefix (activePrefix or href, plus alsoActiveOn).
export function activeHref(links: NavLink[], url: string): string | null {
  const exact = links.find((l) => l.exactOnly && (url === l.href || url === `${l.href}/`));
  if (exact) return exact.href;

  let best: { href: string; length: number } | null = null;
  for (const l of links) {
    const prefixes = [...(l.exactOnly ? [] : [l.activePrefix ?? l.href]), ...(l.alsoActiveOn ?? [])];
    for (const p of prefixes) {
      if (url.startsWith(p) && (!best || p.length > best.length)) best = { href: l.href, length: p.length };
    }
  }
  return best?.href ?? null;
}
